import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const EngagementAnalytics = ({ channel = 'all' }) => {
  const analyticsData = {
    overview: {
      totalSent: 1247,
      delivered: 1189,
      opened: 856,
      clicked: 342,
      responded: 178
    },
    byChannel: {
      sms: { sent: 687, delivered: 672, opened: 672, responded: 124 },
      email: { sent: 560, delivered: 517, opened: 184, clicked: 342, responded: 54 }
    },
    byMessageType: [
      { type: 'Appointment Reminder', sent: 456, openRate: 92.3, responseRate: 45.2, avgResponseTime: '12 min' },
      { type: 'Treatment Update', sent: 234, openRate: 78.6, responseRate: 23.1, avgResponseTime: '2.3 hrs' },
      { type: 'Post-Procedure Instructions', sent: 189, openRate: 85.4, responseRate: 12.7, avgResponseTime: '45 min' },
      { type: 'Medication Reminder', sent: 156, openRate: 88.9, responseRate: 34.6, avgResponseTime: '8 min' },
      { type: 'Follow-Up Care', sent: 123, openRate: 71.5, responseRate: 18.3, avgResponseTime: '1.5 hrs' },
      { type: 'Recall Reminder', sent: 89, openRate: 65.2, responseRate: 28.1, avgResponseTime: '3.2 hrs' }
    ],
    timeAnalysis: [
      { hour: '8 AM', sent: 45, openRate: 72 },
      { hour: '9 AM', sent: 89, openRate: 85 },
      { hour: '10 AM', sent: 123, openRate: 91 },
      { hour: '11 AM', sent: 156, openRate: 88 },
      { hour: '12 PM', sent: 134, openRate: 76 },
      { hour: '1 PM', sent: 98, openRate: 68 },
      { hour: '2 PM', sent: 112, openRate: 82 },
      { hour: '3 PM', sent: 145, openRate: 89 },
      { hour: '4 PM', sent: 167, openRate: 93 },
      { hour: '5 PM', sent: 178, openRate: 87 }
    ]
  };

  const { overview } = analyticsData;
  const deliveryRate = ((overview?.delivered / overview?.totalSent) * 100)?.toFixed(1);
  const openRate = ((overview?.opened / overview?.delivered) * 100)?.toFixed(1);
  const clickRate = ((overview?.clicked / overview?.opened) * 100)?.toFixed(1);
  const responseRate = ((overview?.responded / overview?.totalSent) * 100)?.toFixed(1);

  const maxSent = Math.max(...analyticsData?.timeAnalysis?.map(t => t?.sent));

  return (
    <div className="space-y-6">
      {/* Overview Metrics */}
      <div className="bg-card rounded-xl border border-border shadow-elevation-2 p-4 md:p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg md:text-xl font-semibold text-foreground flex items-center gap-2">
            <Icon name="TrendingUp" size={20} className="text-primary" />
            Engagement Analytics
          </h2>
          <Button variant="outline" size="sm" iconName="Download" iconPosition="left">
            Export Report
          </Button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <div className="bg-muted/50 rounded-lg p-4 border border-border text-center">
            <Icon name="Send" size={24} className="mx-auto mb-2 text-primary" />
            <p className="text-xs text-muted-foreground mb-1">Total Sent</p>
            <p className="text-2xl font-bold text-foreground">{overview?.totalSent}</p>
          </div>
          <div className="bg-success/10 rounded-lg p-4 border border-success/20 text-center">
            <Icon name="CheckCircle" size={24} className="mx-auto mb-2 text-success" />
            <p className="text-xs text-success mb-1">Delivery Rate</p>
            <p className="text-2xl font-bold text-success">{deliveryRate}%</p>
          </div>
          <div className="bg-primary/10 rounded-lg p-4 border border-primary/20 text-center">
            <Icon name="Mail" size={24} className="mx-auto mb-2 text-primary" />
            <p className="text-xs text-primary mb-1">Open Rate</p>
            <p className="text-2xl font-bold text-primary">{openRate}%</p>
          </div>
          <div className="bg-warning/10 rounded-lg p-4 border border-warning/20 text-center">
            <Icon name="MousePointer" size={24} className="mx-auto mb-2 text-warning" />
            <p className="text-xs text-warning mb-1">Click Rate</p>
            <p className="text-2xl font-bold text-warning">{clickRate}%</p>
          </div>
          <div className="bg-error/10 rounded-lg p-4 border border-error/20 text-center">
            <Icon name="MessageCircle" size={24} className="mx-auto mb-2 text-error" />
            <p className="text-xs text-error mb-1">Response Rate</p>
            <p className="text-2xl font-bold text-error">{responseRate}%</p>
          </div>
        </div>

        {/* Channel Breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-muted/30 rounded-lg p-4 border border-border">
            <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="MessageSquare" size={16} className="text-primary" />
              SMS Performance
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Sent</span>
                <span className="text-sm font-semibold text-foreground">{analyticsData?.byChannel?.sms?.sent}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Delivered</span>
                <span className="text-sm font-semibold text-success">{analyticsData?.byChannel?.sms?.delivered}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Responded</span>
                <span className="text-sm font-semibold text-primary">{analyticsData?.byChannel?.sms?.responded}</span>
              </div>
              <div className="pt-2 border-t border-border">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-muted-foreground">Response Rate</span>
                  <span className="text-xs font-semibold text-foreground">
                    {((analyticsData?.byChannel?.sms?.responded / analyticsData?.byChannel?.sms?.sent) * 100)?.toFixed(1)}%
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div 
                    className="bg-primary h-2 rounded-full" 
                    style={{ width: `${(analyticsData?.byChannel?.sms?.responded / analyticsData?.byChannel?.sms?.sent) * 100}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-muted/30 rounded-lg p-4 border border-border">
            <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Mail" size={16} className="text-success" />
              Email Performance
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Sent</span>
                <span className="text-sm font-semibold text-foreground">{analyticsData?.byChannel?.email?.sent}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Delivered</span>
                <span className="text-sm font-semibold text-success">{analyticsData?.byChannel?.email?.delivered}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Opened</span>
                <span className="text-sm font-semibold text-primary">{analyticsData?.byChannel?.email?.opened}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Clicked</span>
                <span className="text-sm font-semibold text-warning">{analyticsData?.byChannel?.email?.clicked}</span>
              </div>
              <div className="pt-2 border-t border-border">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-muted-foreground">Open Rate</span>
                  <span className="text-xs font-semibold text-foreground">
                    {((analyticsData?.byChannel?.email?.opened / analyticsData?.byChannel?.email?.delivered) * 100)?.toFixed(1)}%
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div 
                    className="bg-success h-2 rounded-full" 
                    style={{ width: `${(analyticsData?.byChannel?.email?.opened / analyticsData?.byChannel?.email?.delivered) * 100}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Performance by Message Type */}
      <div className="bg-card rounded-xl border border-border shadow-elevation-2 p-4 md:p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="BarChart3" size={18} className="text-primary" />
          Performance by Message Type
        </h3>
        <div className="space-y-3">
          {analyticsData?.byMessageType?.map((item, index) => (
            <div key={index} className="bg-muted/30 rounded-lg p-4 border border-border">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="font-semibold text-foreground">{item?.type}</p>
                  <p className="text-xs text-muted-foreground">{item?.sent} messages sent</p>
                </div>
                <span className="text-xs text-muted-foreground">Avg response: {item?.avgResponseTime}</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-muted-foreground">Open Rate</span>
                    <span className="text-xs font-semibold text-foreground">{item?.openRate}%</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div className="bg-primary h-2 rounded-full" style={{ width: `${item?.openRate}%` }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-muted-foreground">Response Rate</span>
                    <span className="text-xs font-semibold text-foreground">{item?.responseRate}%</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div className="bg-success h-2 rounded-full" style={{ width: `${item?.responseRate}%` }}></div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Best Time to Send Analysis */}
      <div className="bg-card rounded-xl border border-border shadow-elevation-2 p-4 md:p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Clock" size={18} className="text-primary" />
          Best Time to Send Analysis
        </h3>
        <div className="space-y-2">
          {analyticsData?.timeAnalysis?.map((item, index) => (
            <div key={index} className="flex items-center gap-3">
              <span className="text-sm font-medium text-foreground w-16">{item?.hour}</span>
              <div className="flex-1 bg-muted rounded-full h-8 relative overflow-hidden">
                <div 
                  className="bg-primary/20 h-full" 
                  style={{ width: `${(item?.sent / maxSent) * 100}%` }}
                ></div>
                <div 
                  className="absolute top-0 left-0 bg-success h-full" 
                  style={{ width: `${(item?.sent / maxSent) * (item?.openRate / 100) * 100}%` }}
                ></div>
              </div>
              <span className="text-sm text-muted-foreground w-20 text-right">{item?.sent} sent</span>
              <span className="text-sm font-semibold text-success w-16 text-right">{item?.openRate}%</span>
            </div>
          ))}
        </div>
        <div className="mt-4 flex items-center gap-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-primary/20 rounded"></div>
            <span>Messages Sent</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-success rounded"></div>
            <span>Open Rate</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EngagementAnalytics;